import { create } from 'zustand';
import type { OrderDto, RefundSettlement, ReturnDisposition, ReturnDto, ReturnRejectionReason } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * Returns and refunds.
 *
 * This is the only screen in the admin that moves money, and the API guards it
 * accordingly: a refund is owner-only and cannot exceed what was paid. The
 * state machine refuses to refund before approval, so the UI offers each step
 * in order rather than presenting them all at once.
 *
 * Restocking on receipt is an **explicit choice**, never automatic — a returned
 * item may be damaged, and silently putting it back on sale sells it twice.
 *
 * One return can be open beside the list, read fresh, with the order it came
 * from: the order is how the refund knows which way the money goes back, and
 * who to call. Reading it is best effort — staff who may handle returns but not
 * read orders still see the whole return, just not the payment behind it.
 */

export interface ReturnFilters {
  search: string;
  statuses: string[];
  reason: string;
  /** `YYYY-MM-DD`, when the return was requested. */
  from: string;
  to: string;
}

export const EMPTY_RETURN_FILTERS: ReturnFilters = {
  search: '',
  statuses: [],
  reason: '',
  from: '',
  to: '',
};

interface ReturnsState {
  items: ReturnDto[];
  nextCursor: string | null;
  loading: boolean;
  loadingMore: boolean;
  saving: boolean;
  error: string | null;
  /** Why the last step was refused, shown in the panel that asked for it. */
  actionError: string | null;
  filters: ReturnFilters;
  total: number | null;

  /** The return open beside the list. */
  currentId: string | null;
  current: ReturnDto | null;
  currentLoading: boolean;
  currentError: string | null;
  /** The order behind a return; null until read, or when this person may not read orders. */
  order: OrderDto | null;
  /** The order last asked for, so a late answer for another one is dropped. */
  orderFor: string | null;
  orderLoading: boolean;
  /** Payment providers' names by key, for saying where a refund goes. Owner only, as refunds are. */
  providerNames: Record<string, string> | null;

  load: (filters?: Partial<ReturnFilters>) => Promise<void>;
  loadMore: () => Promise<void>;
  /** `withOrder` false for someone who may not read orders: asking would only be refused. */
  open: (id: string, withOrder: boolean) => Promise<void>;
  close: () => void;
  loadOrder: (orderId: string, force?: boolean) => Promise<void>;
  loadProviderNames: () => Promise<void>;
  clearActionError: () => void;
  approve: (id: string, staffNote?: string) => Promise<boolean>;
  reject: (id: string, rejectionReason: ReturnRejectionReason, staffNote?: string) => Promise<boolean>;
  receive: (
    id: string,
    input: { disposition: ReturnDisposition; restock: boolean; staffNote?: string },
  ) => Promise<boolean>;
  /**
   * Halalas; absent refunds the whole return. Money paid by hand goes back by
   * hand, and says how (`settlement`, ADR-061).
   */
  refund: (id: string, amount?: number, settlement?: RefundSettlement) => Promise<boolean>;
}

const PAGE_SIZE = 20;

type SetState = (partial: Partial<ReturnsState> | ((state: ReturnsState) => Partial<ReturnsState>)) => void;

export const useReturnsStore = create<ReturnsState>((set, get) => ({
  items: [],
  nextCursor: null,
  loading: false,
  loadingMore: false,
  saving: false,
  error: null,
  actionError: null,
  filters: EMPTY_RETURN_FILTERS,
  total: null,
  currentId: null,
  current: null,
  currentLoading: false,
  currentError: null,
  order: null,
  orderFor: null,
  orderLoading: false,
  providerNames: null,

  async load(filters) {
    const next = { ...get().filters, ...filters };
    set({ loading: true, error: null, filters: next });

    try {
      const page = await withSession(() => adminApi.returns.list(toQuery(next, PAGE_SIZE)));
      set({
        items: page.items,
        nextCursor: page.meta.nextCursor,
        total: page.meta.total ?? null,
        loading: false,
      });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load returns.') });
    }
  },

  async loadMore() {
    const { nextCursor, loadingMore, filters, items } = get();
    if (!nextCursor || loadingMore) return;

    set({ loadingMore: true });

    try {
      const page = await withSession(() =>
        adminApi.returns.list({ ...toQuery(filters, PAGE_SIZE), cursor: nextCursor }),
      );
      set({ items: [...items, ...page.items], nextCursor: page.meta.nextCursor, loadingMore: false });
    } catch (error) {
      set({ loadingMore: false, error: errorMessage(error, 'Could not load more returns.') });
    }
  },

  async open(id, withOrder) {
    // The row already on screen shows at once; the fresh read replaces it.
    const known = get().items.find((row) => row.id === id) ?? null;
    set({ currentId: id, current: known, currentLoading: true, currentError: null });
    if (known && withOrder) void get().loadOrder(known.orderId);

    try {
      const fresh = await withSession(() => adminApi.returns.get(id));
      // Another return was opened while this one was on its way.
      if (get().currentId !== id) return;
      set({ current: fresh, currentLoading: false });
      if (!known && withOrder) void get().loadOrder(fresh.orderId);
    } catch (error) {
      if (get().currentId !== id) return;
      set({ currentLoading: false, currentError: errorMessage(error, 'Could not load this return.') });
    }
  },

  close() {
    set({ currentId: null, current: null, currentError: null, actionError: null });
  },

  async loadOrder(orderId, force = false) {
    const { order, orderFor, orderLoading } = get();
    if (!force && orderFor === orderId && (order || orderLoading)) return;
    set({ orderFor: orderId, order: order?.id === orderId ? order : null, orderLoading: true });

    try {
      const fresh = await withSession(() => adminApi.orders.get(orderId));
      if (get().orderFor !== orderId) return;
      set({ order: fresh, orderLoading: false });
    } catch {
      // Not this person's to read, or gone: the return stands without it.
      if (get().orderFor !== orderId) return;
      set({ order: null, orderLoading: false });
    }
  },

  async loadProviderNames() {
    if (get().providerNames) return;
    try {
      const page = await withSession(() => adminApi.payments.providers());
      set({ providerNames: Object.fromEntries(page.items.map((provider) => [provider.key, provider.name])) });
    } catch {
      // The provider's key still names it; the wording falls back to that.
      set({ providerNames: {} });
    }
  },

  clearActionError() {
    set({ actionError: null });
  },

  async approve(id, staffNote) {
    return act(set, () => adminApi.returns.approve(id, staffNote ? { staffNote } : undefined), id, 'approve');
  },

  async reject(id, rejectionReason, staffNote) {
    return act(set, () => adminApi.returns.reject(id, { rejectionReason, staffNote }), id, 'reject');
  },

  async receive(id, input) {
    return act(set, () => adminApi.returns.receive(id, input), id, 'receive');
  },

  async refund(id, amount, settlement) {
    // Absent rather than the full figure: the API then refunds exactly what
    // the return is worth, the one number that cannot be typed wrong.
    const body = { ...(amount ? { amount } : {}), ...(settlement ? { settlement } : {}) };
    const done = await act(
      set,
      () => adminApi.returns.refund(id, Object.keys(body).length > 0 ? body : undefined),
      id,
      'refund',
    );
    // The payment's refunded total moved with it.
    const order = get().order;
    if (done && order) void get().loadOrder(order.id, true);
    return done;
  },
}));

/** Shared shape for the four steps: run it, replace the row and the open return, or report. */
async function act(set: SetState, call: () => Promise<ReturnDto>, id: string, what: string): Promise<boolean> {
  set({ saving: true, actionError: null });

  try {
    const updated = await withSession(call);
    set((state) => ({
      saving: false,
      items: state.items.map((row) => (row.id === id ? updated : row)),
      current: state.currentId === id ? updated : state.current,
    }));
    return true;
  } catch (error) {
    set({ saving: false, actionError: errorMessage(error, `Could not ${what} this return.`) });
    return false;
  }
}

function toQuery(filters: ReturnFilters, limit: number) {
  return {
    limit,
    search: filters.search || undefined,
    status: filters.statuses.length > 0 ? filters.statuses : undefined,
    reason: filters.reason || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
  };
}
